import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td, CapNote } from '../components/ui/bits';
import { useRpc } from '../lib/queries';
import { fmtDateTime } from '../lib/format';

interface SummaryRow { event_type: string; count_24h: number; count_7d: number; count_30d: number; unique_users_30d: number }

export const EventsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const type = params.get('type') ?? '';
  const user = params.get('user') ?? '';
  const days = Number(params.get('days') ?? 7);
  const [draftType, setDraftType] = useState(type);
  const [draftUser, setDraftUser] = useState(user);

  const { data: summary } = useRpc<SummaryRow[]>('events_summary_by_type', { p_prefix: null });
  const { data: rows, error, isLoading } = useRpc<any[]>('admin_events_search', {
    p_type: type || null,
    p_user: user || null,
    p_days: days,
    p_limit: 300,
  });

  const apply = () => {
    const n = new URLSearchParams(params);
    if (draftType) n.set('type', draftType); else n.delete('type');
    if (draftUser) n.set('user', draftUser); else n.delete('user');
    setParams(n, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Events"
        subtitle="Server-side filtered. Event names are load-bearing literals: cockpit funnels match them exactly, so renames in the app break analytics."
        actions={
          <form onSubmit={e => { e.preventDefault(); apply(); }} className="flex items-center gap-2">
            <input
              list="event-types"
              value={draftType}
              onChange={e => setDraftType(e.target.value)}
              placeholder="Event type"
              className="h-9 w-52 px-3 rounded-md border border-stone-300 bg-white focus:border-indigo-400 focus:outline-none text-[13px]"
            />
            <datalist id="event-types">
              {(summary ?? []).map(s => <option key={s.event_type} value={s.event_type} />)}
            </datalist>
            <input
              value={draftUser}
              onChange={e => setDraftUser(e.target.value)}
              placeholder="User id"
              className="h-9 w-48 px-3 rounded-md border border-stone-300 bg-white focus:border-indigo-400 focus:outline-none text-[13px]"
            />
            <select
              value={String(days)}
              onChange={e => { const n = new URLSearchParams(params); n.set('days', e.target.value); setParams(n, { replace: true }); }}
              className="h-9 px-2 rounded-md border border-stone-300 bg-white text-[13px]"
            >
              <option value="1">24 hours</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
            <Button type="submit" variant="primary">Filter</Button>
          </form>
        }
      />

      <div className="grid md:grid-cols-3 gap-3">
        <Section title="Event types" sub="Counts over 24 hours, 7 and 30 days. Click a row to filter the stream.">
          <TableWrap>
            <thead><tr><Th>Type</Th><Th>24h</Th><Th>7d</Th><Th>30d</Th></tr></thead>
            <tbody>
              {(summary ?? []).slice(0, 40).map(s => (
                <tr
                  key={s.event_type}
                  className="cursor-pointer hover:bg-stone-50"
                  onClick={() => { setDraftType(s.event_type); const n = new URLSearchParams(params); n.set('type', s.event_type); setParams(n, { replace: true }); }}
                >
                  <Td className="font-medium text-stone-900">{s.event_type}</Td>
                  <Td className="tabular-nums">{s.count_24h}</Td>
                  <Td className="tabular-nums">{s.count_7d}</Td>
                  <Td className="tabular-nums">{s.count_30d}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Section>

        <Section title={`Stream${type ? `: ${type}` : ''}`} className="md:col-span-2">
          {isLoading && <LoadingRow />}
          {error && <ErrorPanel error={error} />}
          {!isLoading && !error && ((rows ?? []).length === 0 ? <EmptyNote>Nothing matches</EmptyNote> : (
            <>
              <div className="space-y-1 max-h-[640px] overflow-y-auto">
                {(rows ?? []).map((e, i) => (
                  <details key={e.id ?? i} className="text-[12px] border-b border-stone-50 last:border-0">
                    <summary className="cursor-pointer flex items-center gap-2 py-1.5 hover:bg-stone-50 rounded px-1">
                      <span className="font-medium text-stone-800 flex-1">{e.event_type}</span>
                      <UserLink id={e.user_id} />
                      <span className="text-stone-400 whitespace-nowrap">{fmtDateTime(e.created_at)}</span>
                    </summary>
                    <pre className="text-[10.5px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600 mb-1.5">{JSON.stringify(e.event_data, null, 2)}</pre>
                  </details>
                ))}
              </div>
              <CapNote shown={(rows ?? []).length} cap={300} />
            </>
          ))}
        </Section>
      </div>
    </div>
  );
};
